import { useParams, useSearchParams } from 'react-router-dom';
import { Routes, routeBuilders } from '../../../routes';
import { PageBreadcrumb } from '../../../shared/components/layout/PageBreadcrumb';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Snackbar,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import {
  PermissionGate,
  Permissions,
  usePermission,
} from '@billda/permissions-ui';
import { FeatureSwitchGate, Features, useFeature } from '@billda/features-ui';
import { useSiteDetails } from '../hooks/useSiteDetails';
import { useFolderContents } from '../hooks/useFolderContents';
import { useEscapeKey } from '../../../shared/hooks/useEscapeKey';
import { useSitePanelState } from '../hooks/useSitePanelState';
import { SiteBurndownChart } from '../components/SiteBurndownChart';
import { SiteDetailsHeader } from '../components/v2/organisms/SiteDetailsHeader';
import { SiteEventsTimeline } from '../components/timeline/organisms/SiteEventsTimeline';
import { SitePlotsLedger } from '../ledger/SitePlotsLedger';
import { SitePlotsDisplayMenu } from '../components/grouped/SitePlotsDisplayMenu';
import { useSiteTableStore, type RowType } from '../stores/siteTableStore';
import { backfillSiteCoordinates } from '../api/sitesApi';
import { sitesKeys } from '../hooks/useSites';
import { SidePanel, ResizableHandle } from '../../../shared/components/panel';
import { PlotDetailsPanel } from '../components/panel/organisms/PlotDetailsPanel';
import { LabourItemDetailsPanel } from '../components/panel/organisms/LabourItemDetailsPanel';
import { VariationDetailsPanel } from '../components/panel/organisms/VariationDetailsPanel';
import { AllocationDetailsPanel } from '../components/panel/organisms/AllocationDetailsPanel';
import { AddLabourItemPanel } from '../components/panel/organisms/AddLabourItemPanel';
import { AddPlotPanel } from '../components/panel/organisms/AddPlotPanel';
import { SiteReimportPanel } from '../components/panel/organisms/SiteReimportPanel';
import { useSiteReimportStore } from '../stores/siteReimportStore';
import { SiteFileList } from '../components/files/SiteFileList';
import { FolderBrowser } from '../components/files/FolderBrowser';
import { useCreateFolder } from '../hooks/useFolderMutations';
import { useBulkDelete } from '../hooks/useBulkDelete';
import { useFileSelectionStore } from '../stores/fileSelectionStore';
import { AdminCancelAllocationPanel } from '../components/panel/organisms/AdminCancelAllocationPanel';
import { FileDetailPanel } from '../components/files/FileDetailPanel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import { SITES_HELP } from '../../../shared/constants/helpContent';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs, type ChipTab } from '../../../shared/components/ui/ChipTabs';
import ContextMenu from '../../../shared/components/ui/ContextMenu';

export default function SitePage() {
  const { id } = useParams<{ id: string }>();
  const siteId = id ? parseInt(id, 10) : null;

  const { data: siteData, error } = useSiteDetails(siteId);
  const loading = !siteData && !error; // Initial loading only
  const {
    isOpen,
    type,
    id: panelId,
    closePanel,
    openPanel,
  } = useSitePanelState();

  // Help panel state
  const [helpOpen, setHelpOpen] = useState<boolean>(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<
    'overview' | 'plots' | 'data' | 'files'
  >('overview');
  const [showValues, setShowValues] = useState(true);
  const [plotSearch, setPlotSearch] = useState('');
  const hasImportPermission = usePermission(Permissions.Sites.Import);
  const isFilesEnabled = useFeature(Features.Files.IsEnabled);
  const isEnhancedFileManager = useFeature(
    Features.Files.IsEnhancedFileManagerEnabled
  );
  const hasFilesWritePermission = usePermission(Permissions.Files.Write);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const numericSiteId = siteId ?? 0;
  const createFolderMutation = useCreateFolder(numericSiteId);
  const bulkDelete = useBulkDelete(numericSiteId);
  const selectedItems = useFileSelectionStore(state => state.selectedItems);
  const getSelectedItems = useFileSelectionStore(
    state => state.getSelectedItems
  );
  const selectionCount = selectedItems.size;
  const currentFolderId = searchParams.get('folderId') ?? undefined;

  // Folder trail for the files-tab breadcrumb — same query FolderBrowser
  // runs, so the React Query cache is shared and no extra request is made.
  const { data: folderContents } = useFolderContents(
    numericSiteId,
    currentFolderId,
    {
      enabled:
        activeTab === 'files' &&
        isEnhancedFileManager &&
        Boolean(currentFolderId),
    }
  );

  const handleCreateFolder = useCallback(() => {
    const trimmed = newFolderName.trim();
    if (!trimmed) {
      return;
    }
    createFolderMutation.mutate(
      { name: trimmed, parentFolderId: currentFolderId },
      {
        onSuccess: () => {
          setCreateFolderOpen(false);
          setNewFolderName('');
        },
      }
    );
  }, [newFolderName, currentFolderId, createFolderMutation]);

  const handleBulkDelete = () => {
    const items = getSelectedItems();
    const fileIds = items.filter(i => i.type === 'file').map(i => i.id);
    const folderIds = items.filter(i => i.type === 'folder').map(i => i.id);
    bulkDelete.mutate(
      { fileIds, folderIds },
      { onSuccess: () => setDeleteConfirmOpen(false) }
    );
  };

  // Build tabs dynamically
  const tabs = useMemo<ChipTab[]>(() => {
    const base: ChipTab[] = [
      { label: 'Overview', value: 'overview' },
      { label: 'Plots', value: 'plots' },
    ];
    if (hasImportPermission) {
      base.push({ label: 'Data', value: 'data' });
    }
    if (isFilesEnabled) {
      base.push({ label: 'Files', value: 'files' });
    }
    return base;
  }, [hasImportPermission, isFilesEnabled]);

  // Backfill coordinates state
  const queryClient = useQueryClient();
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [backfillSnackbar, setBackfillSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleBackfillCoordinates = useCallback(async () => {
    if (!siteData) return;
    setIsBackfilling(true);
    try {
      await backfillSiteCoordinates(siteData.id);
      await queryClient.invalidateQueries({
        queryKey: sitesKeys.detail(siteData.id),
      });
      setBackfillSnackbar({
        open: true,
        message: 'Coordinates backfilled successfully.',
        severity: 'success',
      });
    } catch {
      setBackfillSnackbar({
        open: true,
        message: 'Failed to backfill coordinates.',
        severity: 'error',
      });
    } finally {
      setIsBackfilling(false);
    }
  }, [siteData, queryClient]);

  // Reimport success snackbar
  const reimportSuccessMessage = useSiteReimportStore(
    state => state.successMessage
  );
  const clearReimportSuccess = useSiteReimportStore(
    state => state.clearSuccessMessage
  );

  // Sync URL panel state with table selection store
  const setSelectedRow = useSiteTableStore(state => state.setSelectedRow);

  useEffect(() => {
    if (type && panelId) {
      // Map panel type to row type
      const rowTypeMap: Record<string, RowType> = {
        plot: 'plot',
        labour: 'labourItem',
        variation: 'variation',
        allocation: 'allocation',
      };
      const rowType = rowTypeMap[type];
      if (rowType) {
        setSelectedRow(panelId, rowType);
      }
    } else {
      setSelectedRow(null, null);
    }
  }, [type, panelId, setSelectedRow]);

  // ESC key handler
  useEscapeKey(closePanel, isOpen);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress data-testid="site-loading-spinner" />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Unable to load site details: {error.message}
      </Alert>
    );
  }

  // Not found state
  if (!siteData) {
    return <Alert severity="warning">Site not found</Alert>;
  }

  const handleTabChange = (v: string) => {
    setActiveTab(v as 'overview' | 'plots' | 'data' | 'files');
  };

  // Header breadcrumb — walks the folder path while browsing files,
  // otherwise just Sites › {site name}.
  const folderTrail =
    activeTab === 'files' && isEnhancedFileManager && currentFolderId
      ? folderContents?.breadcrumb
      : undefined;

  const breadcrumbSegments: { label: string; to?: string }[] = [
    { label: 'Sites', to: Routes.sites.manage },
  ];
  if (folderTrail) {
    const siteRoute = routeBuilders.sites.detail(String(siteData.id));
    folderTrail.forEach((item, index) => {
      if (index === folderTrail.length - 1) {
        breadcrumbSegments.push({ label: item.name });
      } else if (item.id === null) {
        breadcrumbSegments.push({ label: item.name, to: siteRoute });
      } else {
        breadcrumbSegments.push({
          label: item.name,
          to: `${siteRoute}?folderId=${item.id}`,
        });
      }
    });
  } else {
    breadcrumbSegments.push({ label: siteData.name });
  }

  // Build panel content for PageContentArea
  const panelContent = (
    <>
      {isOpen && type && panelId && (
        <>
          <ResizableHandle />
          <SidePanel
            open={isOpen}
            onClose={closePanel}
            title={getPanelTitle(type)}
          >
            {renderPanelContent(type, panelId, siteData.id)}
          </SidePanel>
        </>
      )}
      {helpOpen && (
        <>
          <ResizableHandle />
          <SidePanel
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            title="Help"
            testId="sites-help-drawer"
          >
            <HelpPanel helpContent={SITES_HELP} />
          </SidePanel>
        </>
      )}
    </>
  );

  return (
    <>
      {/* Create folder dialog */}
      <Dialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder name"
            value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
            sx={{ mt: 1 }}
            data-testid="create-folder-name-input"
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim() || createFolderMutation.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        data-testid="bulk-delete-confirm-dialog"
      >
        <DialogTitle>
          Delete {selectionCount} {selectionCount === 1 ? 'item' : 'items'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {`They'll be moved to the recycle bin.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            data-testid="bulk-delete-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            color="error"
            variant="contained"
            disabled={bulkDelete.isPending}
            data-testid="bulk-delete-confirm-button"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <PageContentArea isPanelOpen={isOpen || helpOpen} panel={panelContent}>
        <PageHeader
          title={<PageBreadcrumb segments={breadcrumbSegments} />}
          helpAction={{
            onClick: () => setHelpOpen(true),
            testId: 'sites-help-button',
          }}
          actions={
            <PermissionGate permission={Permissions.Labour.Edit}>
              <PageHeaderAction
                label="Add Plot"
                icon={<AddIcon />}
                onClick={() => openPanel('add-plot', siteData.id.toString())}
                data-testid="add-plot-button"
              />
            </PermissionGate>
          }
        />

        <PageToolbar
          tabs={
            <ChipTabs
              tabs={tabs}
              value={activeTab}
              onChange={handleTabChange}
            />
          }
          searchValue={activeTab === 'plots' ? plotSearch : undefined}
          onSearchChange={activeTab === 'plots' ? setPlotSearch : undefined}
          actions={
            activeTab === 'plots' ? (
              <SitePlotsDisplayMenu
                showValues={showValues}
                onShowValuesChange={setShowValues}
              />
            ) : activeTab === 'files' && hasFilesWritePermission ? (
              <>
                {isEnhancedFileManager && (
                  <>
                    <PageHeaderAction
                      label="Delete"
                      icon={<DeleteOutlineIcon />}
                      onClick={() => setDeleteConfirmOpen(true)}
                      disabled={selectionCount === 0}
                      data-testid="bulk-delete-button"
                    />
                    <PageHeaderAction
                      label="New Folder"
                      icon={<CreateNewFolderOutlinedIcon />}
                      onClick={() => setCreateFolderOpen(true)}
                      data-testid="create-folder-button"
                    />
                  </>
                )}
                {isEnhancedFileManager ? (
                  <PageHeaderAction
                    label="Upload"
                    icon={<UploadFileIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={e => setUploadMenuAnchor(e.currentTarget)}
                    data-testid="upload-file-button"
                    aria-haspopup="true"
                    aria-expanded={Boolean(uploadMenuAnchor)}
                  />
                ) : (
                  <PageHeaderAction
                    label="Upload"
                    icon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-file-button"
                  />
                )}
              </>
            ) : undefined
          }
          hideFilter={activeTab !== 'plots'}
        />

        {activeTab === 'overview' ? (
          <Box sx={{ p: { xs: 2, sm: 3 }, overflow: 'auto', flex: 1 }}>
            <SiteDetailsHeader
              site={{
                id: siteData.id,
                name: siteData.name,
                slug: siteData.slug,
                what3WordsAddress: siteData.what3WordsAddress,
                latitude: siteData.latitude,
                longitude: siteData.longitude,
              }}
              financialMetrics={
                siteData.financialMetrics
                  ? {
                      totalLabourPrice:
                        siteData.financialMetrics.totalLabourPrice,
                      approvedAmount: siteData.financialMetrics.approvedAmount,
                      bookedAmount: siteData.financialMetrics.bookedAmount,
                      remaining: siteData.financialMetrics.remaining,
                      progress: siteData.financialMetrics.progress,
                    }
                  : null
              }
              plotCount={siteData.plots?.length}
              isLoading={loading}
              onBackfillCoordinates={handleBackfillCoordinates}
              isBackfilling={isBackfilling}
            />
            <FeatureSwitchGate feature={Features.Sites.IsBurndownChartEnabled}>
              <PermissionGate permission={Permissions.Sites.Financial.Read}>
                <Box sx={{ mt: 2 }}>
                  <SiteBurndownChart siteId={siteData.id} />
                </Box>
              </PermissionGate>
            </FeatureSwitchGate>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  gutterBottom
                >
                  Activity
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <SiteEventsTimeline siteId={siteData.id} />
              </CardContent>
            </Card>
          </Box>
        ) : activeTab === 'data' ? (
          <PermissionGate permission={Permissions.Sites.Import}>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <SiteReimportPanel
                siteId={siteData.id}
                onClose={() => setActiveTab('overview')}
              />
            </Box>
          </PermissionGate>
        ) : activeTab === 'files' ? (
          <PermissionGate permission={Permissions.Files.Read}>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ContextMenu
                open={Boolean(uploadMenuAnchor)}
                onClose={() => setUploadMenuAnchor(null)}
                anchorEl={uploadMenuAnchor}
                items={[
                  {
                    key: 'upload-files',
                    label: 'Upload files',
                    icon: <UploadFileIcon fontSize="small" />,
                    onClick: () => fileInputRef.current?.click(),
                  },
                  {
                    key: 'upload-folder',
                    label: 'Upload folder',
                    icon: <DriveFolderUploadOutlinedIcon fontSize="small" />,
                    onClick: () => folderInputRef.current?.click(),
                  },
                ]}
              />
              {isEnhancedFileManager ? (
                <FolderBrowser
                  siteId={siteData.id}
                  folderId={searchParams.get('folderId') ?? undefined}
                  onNavigate={folderId => {
                    if (folderId) {
                      setSearchParams({ folderId });
                    } else {
                      setSearchParams({});
                    }
                  }}
                  canWrite={hasFilesWritePermission}
                  uploadButtonRef={fileInputRef}
                  folderInputRef={folderInputRef}
                  hideBreadcrumb
                  onCreateFolder={() => setCreateFolderOpen(true)}
                />
              ) : (
                <SiteFileList
                  siteId={siteData.id}
                  uploadButtonRef={fileInputRef}
                />
              )}
            </Box>
          </PermissionGate>
        ) : (
          <PermissionGate permission={Permissions.Plots.Read}>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <SitePlotsLedger
                siteId={siteData.id}
                showValues={showValues}
                search={plotSearch}
              />
            </Box>
          </PermissionGate>
        )}
      </PageContentArea>

      {/* Reimport success snackbar */}
      <Snackbar
        open={!!reimportSuccessMessage}
        autoHideDuration={6000}
        onClose={clearReimportSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clearReimportSuccess}
          severity="success"
          variant="filled"
        >
          {reimportSuccessMessage}
        </Alert>
      </Snackbar>

      {/* Backfill coordinates snackbar */}
      <Snackbar
        open={backfillSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setBackfillSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() =>
            setBackfillSnackbar(prev => ({ ...prev, open: false }))
          }
          severity={backfillSnackbar.severity}
          variant="filled"
        >
          {backfillSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// Helper function to get panel title based on type
function getPanelTitle(type: string): string {
  switch (type) {
    case 'plot':
      return 'Plot Details';
    case 'labour':
      return 'Labour Item Details';
    case 'add-labour-item':
      return 'Add Labour Item';
    case 'add-plot':
      return 'Add Plot';
    case 'allocation':
      return 'Allocation Details';
    case 'variation':
      return 'Variation Details';
    case 'admin-cancel':
      return 'Cancel Allocation';
    case 'file':
      return 'File Details';
    default:
      return 'Details';
  }
}

// Wrapper components that call hooks and pass isFetching
function PlotDetailsPanelWrapper({ plotId }: { plotId: number }) {
  return <PlotDetailsPanel plotId={plotId} />;
}

function LabourItemDetailsPanelWrapper({
  labourItemId,
}: {
  labourItemId: number;
}) {
  return <LabourItemDetailsPanel labourItemId={labourItemId} />;
}

function AllocationDetailsPanelWrapper({
  allocationId,
}: {
  allocationId: string;
}) {
  const { openPanel } = useSitePanelState();
  return (
    <AllocationDetailsPanel
      allocationId={allocationId}
      onAdminCancel={id => openPanel('admin-cancel', id)}
    />
  );
}

function VariationDetailsPanelWrapper({
  variationId,
}: {
  variationId: string;
}) {
  return <VariationDetailsPanel variationId={variationId} />;
}

function AdminCancelAllocationPanelWrapper({
  allocationId,
}: {
  allocationId: string;
}) {
  const { openPanel } = useSitePanelState();
  return (
    <AdminCancelAllocationPanel
      allocationId={allocationId}
      onBack={() => openPanel('allocation', allocationId)}
    />
  );
}

function FileDetailPanelWrapper({
  fileId,
  siteId,
}: {
  fileId: string;
  siteId: number;
}) {
  const { closePanel } = useSitePanelState();
  return (
    <FileDetailPanel fileId={fileId} siteId={siteId} onClose={closePanel} />
  );
}

// Helper function to render panel content based on type
function renderPanelContent(
  type: string,
  id: string,
  siteId: number
): React.ReactNode {
  // Handle plot panel
  if (type === 'plot') {
    const plotId = parseInt(id, 10);
    if (!isNaN(plotId)) {
      return <PlotDetailsPanelWrapper key={plotId} plotId={plotId} />;
    }
  }

  // Handle labour panel
  if (type === 'labour') {
    const labourItemId = parseInt(id, 10);
    if (!isNaN(labourItemId)) {
      return (
        <LabourItemDetailsPanelWrapper
          key={labourItemId}
          labourItemId={labourItemId}
        />
      );
    }
  }

  // Handle add labour item panel
  if (type === 'add-labour-item') {
    const plotId = parseInt(id, 10);
    if (!isNaN(plotId)) {
      return <AddLabourItemPanel plotId={plotId} />;
    }
  }

  // Handle add plot panel
  if (type === 'add-plot') {
    const siteId = parseInt(id, 10);
    if (!isNaN(siteId)) {
      return <AddPlotPanel siteId={siteId} />;
    }
  }

  // Handle variation panel
  if (type === 'variation') {
    return <VariationDetailsPanelWrapper key={id} variationId={id} />;
  }

  // Handle allocation panel
  if (type === 'allocation') {
    return <AllocationDetailsPanelWrapper key={id} allocationId={id} />;
  }

  // Handle admin cancel panel
  if (type === 'admin-cancel') {
    return <AdminCancelAllocationPanelWrapper allocationId={id} />;
  }

  // Handle file detail panel
  if (type === 'file') {
    return <FileDetailPanelWrapper fileId={id} siteId={siteId} />;
  }

  // Placeholder for other panel types
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: 3,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Panel Type: {type}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        ID: {id}
      </Typography>
    </Box>
  );
}
